// Every string of the public home page, in both languages of the app. The
// page reads one block, picked from the language in the URL, never the
// browser's: a visitor on /fr gets French words and French number formats.

const fr = {
  documentTitle: "Collecte vocale en langues ouest-africaines",
  hero: {
    eyebrow: "Yodi Data Hub, par Umbaji",
    title: "Votre langue mérite",
    titleAccent: "sa place dans l'IA.",
    body:
      "Enregistrez, transcrivez, traduisez et validez des phrases dans votre langue. Chaque contribution validée enrichit des données ouvertes pour l'IA africaine, et elle est rémunérée.",
    primary: "Commencer à contribuer",
    secondary: "J'ai déjà un compte",
    ownSpace: "Aller à mon espace",
    liveLabel: "En direct",
    recordings: (n: string) => `${n} enregistrements collectés`,
    recordingsUnit: "enregistrements collectés",
    recordingsLoading: "Chargement des chiffres",
    languagesLabel: "Langues de la collecte",
    openTitle: "Collecte ouverte",
    openBody: "Enregistrez, transcrivez, traduisez et validez dans votre langue.",
    cardFoot: "Chiffres mis à jour toutes les 5 minutes",
  },
  figures: {
    eyebrow: "La plateforme en chiffres",
    title: "Une collecte vivante, mesurée en direct.",
    recordings: "Enregistrements collectés",
    hours: "Heures enregistrées",
    contributors: "Contributeurs inscrits",
    languages: "Langues avec contributions",
    countries: "Pays de collecte",
    hoursUnit: "h",
    error: "Les chiffres ne sont pas disponibles pour le moment.",
    retry: "Réessayer",
  },
  how: {
    eyebrow: "Comment ça marche",
    title: "Trois étapes, à votre rythme.",
    steps: [
      ["Créez votre compte", "Une adresse e-mail ou un compte Google suffit. Choisissez les langues que vous parlez."],
      ["Contribuez quand vous voulez", "Quelques minutes sur ordinateur ou sur téléphone : lire, écouter, écrire, vérifier."],
      ["Soyez payé après validation", "Vos contributions validées sont rémunérées, par Mobile Money."],
    ] as [string, string][],
  },
  ways: {
    eyebrow: "Quatre façons de contribuer",
    title: "Choisissez ce que vous aimez faire.",
    cta: "Commencer",
  },
  languages: {
    eyebrow: "Les langues de la collecte",
    title: "Chaque langue avance avec sa communauté.",
    collected: "enregistrements",
    validated: (pct: string) => `${pct} validés`,
    empty: "Les premières langues apparaîtront ici dès les premières contributions.",
  },
  leaders: {
    eyebrow: "Classement de la semaine",
    title: "Les contributeurs les plus actifs.",
    contributionsUnit: "contributions",
    empty: "Personne n'a encore contribué cette semaine. La première place est libre.",
    error: "Le classement n'est pas disponible pour le moment.",
    seeAll: "Voir le classement complet",
  },
  map: {
    eyebrow: "Où se fait la collecte",
    title: "Des voix venues de toute l'Afrique de l'Ouest.",
    collectionTitle: "Lieux de collecte",
    collectionSubtitle: "Contributions par pays où elles ont été enregistrées",
    profileTitle: "Pays des contributeurs",
    profileSubtitle: "Pays déclaré dans le profil",
    collectionUnit: "contributions",
    profileUnit: "contributeurs",
    countLabel: "pays",
  },
  faq: {
    eyebrow: "Questions fréquentes",
    title: "Ce qu'on nous demande souvent.",
  },
  final: {
    title: "Prêt à faire entendre votre langue ?",
    body: "Créez votre compte en une minute et faites votre première contribution aujourd'hui.",
    cta: "Créer mon compte",
  },
};

export type PublicHomeCopy = typeof fr;

const en: PublicHomeCopy = {
  documentTitle: "Voice data collection in West African languages",
  hero: {
    eyebrow: "Yodi Data Hub, by Umbaji",
    title: "Your language deserves",
    titleAccent: "its place in AI.",
    body:
      "Record, transcribe, translate and validate sentences in your language. Every validated contribution grows open data for African AI, and it is paid.",
    primary: "Start contributing",
    secondary: "I already have an account",
    ownSpace: "Go to my space",
    liveLabel: "Live",
    recordings: (n: string) => `${n} recordings collected`,
    recordingsUnit: "recordings collected",
    recordingsLoading: "Loading the figures",
    languagesLabel: "Languages being collected",
    openTitle: "Collection is open",
    openBody: "Record, transcribe, translate and validate in your language.",
    cardFoot: "Figures refreshed every 5 minutes",
  },
  figures: {
    eyebrow: "The platform in numbers",
    title: "A living collection, measured live.",
    recordings: "Recordings collected",
    hours: "Hours recorded",
    contributors: "Registered contributors",
    languages: "Languages with contributions",
    countries: "Collection countries",
    hoursUnit: "h",
    error: "The figures are not available right now.",
    retry: "Try again",
  },
  how: {
    eyebrow: "How it works",
    title: "Three steps, at your own pace.",
    steps: [
      ["Create your account", "An email address or a Google account is enough. Pick the languages you speak."],
      ["Contribute whenever you like", "A few minutes on a computer or a phone: read, listen, write, check."],
      ["Get paid after validation", "Your validated contributions are paid, through Mobile Money."],
    ],
  },
  ways: {
    eyebrow: "Four ways to contribute",
    title: "Pick what you enjoy doing.",
    cta: "Get started",
  },
  languages: {
    eyebrow: "Languages being collected",
    title: "Each language grows with its community.",
    collected: "recordings",
    validated: (pct: string) => `${pct} validated`,
    empty: "The first languages will show here as soon as contributions come in.",
  },
  leaders: {
    eyebrow: "This week's leaderboard",
    title: "The most active contributors.",
    contributionsUnit: "contributions",
    empty: "Nobody has contributed yet this week. First place is open.",
    error: "The leaderboard is not available right now.",
    seeAll: "See the full leaderboard",
  },
  map: {
    eyebrow: "Where the collection happens",
    title: "Voices from all over West Africa.",
    collectionTitle: "Collection places",
    collectionSubtitle: "Contributions by the country they were recorded in",
    profileTitle: "Contributor countries",
    profileSubtitle: "Country given in the profile",
    collectionUnit: "contributions",
    profileUnit: "contributors",
    countLabel: "countries",
  },
  faq: {
    eyebrow: "Frequently asked questions",
    title: "What people often ask us.",
  },
  final: {
    title: "Ready to make your language heard?",
    body: "Create your account in a minute and make your first contribution today.",
    cta: "Create my account",
  },
};

export function publicHomeCopy(language: string): PublicHomeCopy {
  return language === "en" ? en : fr;
}
